export interface ExpandParams {
    $select?: string[];
    $top?: number;
    $count?: boolean;
    $filter?: string;
    $expand?: ExpandItem[];
    [key: string]: string[] | number | boolean | string | ExpandItem[] | undefined;
}

export interface ExpandItem {
    entity: string;
    params: ExpandParams;
}

function splitAtDepthZero(input: string, delimiter: string): string[] {
    const parts: string[] = [];
    let current = "";
    let depth = 0;

    for (const char of input) {
        if (char === "(") {
            depth++;
        } else if (char === ")") {
            depth--;
        }

        if (char === delimiter && depth === 0) {
            parts.push(current);
            current = "";
        } else {
            current += char;
        }
    }

    if (current) {
        parts.push(current);
    }

    return parts;
}

function splitFirstEquals(segment: string): [string, string] | null {
    const idx = segment.indexOf("=");
    if (idx === -1) {
        return null;
    }

    return [segment.slice(0, idx), segment.slice(idx + 1)];
}

export function parseExpand(expandString: string): ExpandItem[] {
    if (!expandString) {
        return [];
    }

    const parts = splitAtDepthZero(expandString, ",")
        .map(p => p.trim())
        .filter(Boolean);

    return parts
        .map<ExpandItem | null>(part => {
            const match = /^([^(]+)(?:\((.*)\))?$/.exec(part);
            if (!match) {
                return null;
            }

            const [, entity, rawParams] = match;
            const params: ExpandParams = {};

            if (rawParams) {
                const segments = splitAtDepthZero(rawParams, ";");

                for (const segment of segments) {
                    const pair = splitFirstEquals(segment);
                    if (!pair) {
                        continue;
                    }

                    const [key, value] = pair;
                    if (!key || !value) {
                        continue;
                    }

                    if (key === "$select") {
                        params[key] = value.split(",");
                    } else if (key === "$expand") {
                        params.$expand = parseExpand(value);
                    } else if (value === "true" || value === "false") {
                        params[key] = value === "true";
                    } else if (!isNaN(Number(value))) {
                        params[key] = Number(value);
                    } else {
                        params[key] = value;
                    }
                }
            }

            return { entity: entity.trim(), params };
        })
        .filter((item): item is ExpandItem => item !== null);
}
